import { useEffect, useState } from 'react';
import { useUser } from '../../../hooks/useUser';
import { useAgency } from '../../../hooks/useAgency';
import { Toast } from '@components';
import { PageLoader } from '../../../components/PageLoader/PageLoader';
import AgencyInfoView from './AgencyInfoView';
import { AgenciesService, deleteAgencyQuestion, getAgency } from '../../../networkRequest/agenciesService';
import { Logger } from '../../../utils/logger';
import AgencyDialogForm from '../../../pages/SurePassAdminPage/components/AgencyDialogForm';
import { AgencyData, Agency, AgencyQuestion } from '../../../utils/types';
import DeleteVerificationQuestionDialog from './DeleteVerificationQuestionDialog';

const AgencyInfo = () => {
  const { user } = useUser();
  const { agency } = useAgency();
  const [agencyInfo, setAgencyInfo] = useState<Agency | null>(null);
  const [agencyQuestions, setAgencyQuestions] = useState<AgencyQuestion[] | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [editAgencyDialogOpen, setEditAgencyDialogOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [agencyQuestionIdPendingDelete, setAgencyQuestionIdPendingDelete] = useState<string | null>(null)

  const fetchAgencyData = async () => {
    if (user && agency) {
      try {
        const agencyPromise = getAgency(user.accessToken, agency.id)
        const aqsPromise = AgenciesService.getAgencyQuestions(user.accessToken, agency.id);
        const agencyResponse = await agencyPromise;
        const aqsResponse = await aqsPromise;
        if (!agencyResponse.data || agencyResponse.error) {
          throw new Error(agencyResponse.error || 'data is missing');
        }
        if (!aqsResponse.data || aqsResponse.error) {
          throw new Error(aqsResponse.error || 'data is missing');
        }
        setAgencyInfo(agencyResponse.data);
        setAgencyQuestions(aqsResponse.data);
      } catch (error: any) {
        if (error.message) {
          Logger.error(error.message, {})
          setErrorMessage(error.message);
        } else {
          const message = error.toString();
          Logger.error(message, {})
          setErrorMessage(message);
        }
      } finally {
        setLoading(false);
      }
    }
  }

  const handleFormResponse = (agency: AgencyData) => {
    setAgencyInfo(agency)
  }

  const onAgencyQuestionCreated = (agencyQuestion: AgencyQuestion) => {
    setAgencyQuestions(prevQuestions => {
      if (prevQuestions) {
        return [...prevQuestions, agencyQuestion]
      } else {
        return [agencyQuestion]
      }
    })
    setSuccessMessage("Verification Question created successfully")
  }

  const onAgencyQuestionDeleted = async () => {
    if (!user?.accessToken || !agencyQuestionIdPendingDelete) return;

    const result = await deleteAgencyQuestion(user.accessToken, agencyQuestionIdPendingDelete)
    if (result.error) {
      setErrorMessage(result.error)
      return;
    }

    setAgencyQuestions(prevQuestions => {
      if (prevQuestions) {
        return prevQuestions.filter(question => question.id !== agencyQuestionIdPendingDelete)
      } else {
        return null
      }
    });

    setAgencyQuestionIdPendingDelete(null)
  }

  useEffect(() => {
    fetchAgencyData();
  }, [user, agency]);

  if (loading) {
    return <PageLoader />;
  }

  if (!agencyInfo || agencyQuestions === null) {
    return <>
      {errorMessage && (
        <Toast type="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}
      {successMessage && (
        <Toast type="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
    </>
  }

  return (
    <>
      <AgencyInfoView
        agencyInfo={agencyInfo}
        setData={setAgencyInfo}
        setError={setErrorMessage}
        setEditDialogOpen={setEditAgencyDialogOpen}
        agencyId={agency?.id}
        agencyQuestions={agencyQuestions}
        onAgencyQuestionCreated={onAgencyQuestionCreated}
        setAgencyQuestionIdPendingDelete={setAgencyQuestionIdPendingDelete}
      />
      <AgencyDialogForm
        open={editAgencyDialogOpen}
        setDialogOpen={setEditAgencyDialogOpen}
        setSuccessMessage={setSuccessMessage}
        handleResponse={handleFormResponse}
        agency={agencyInfo}
      />
      <DeleteVerificationQuestionDialog
        open={!!agencyQuestionIdPendingDelete}
        onClose={() => setAgencyQuestionIdPendingDelete(null)}
        onDelete={onAgencyQuestionDeleted}
      />
      {errorMessage && (
        <Toast type="error" onClose={() => setErrorMessage('')}>
          {errorMessage}
        </Toast>
      )}
      {successMessage && (
        <Toast type="success" onClose={() => setSuccessMessage('')}>
          {successMessage}
        </Toast>
      )}
    </>
  );
};

export default AgencyInfo;
